// Shown inside the Drawer on the right.
// Shows image metadata (log name, timestamp, etc.), relevant links, and also the 'Sent to Annotations' status if applicable.

import { FC, useMemo } from 'react';
import styled from '@emotion/styled';
import {
  KeyValueTable,
  KeyValueTableRow,
} from 'packages/lib/common-ui/keyValueTable';
import { Typography } from 'antd';
import { OmnitagResultItem } from 'packages/lib/api/omnitag/contract/omnitag-search';

// Pylon URL param reference:
// https://confluence.ci.motional.com/confluence/display/BACK/Pylon+URL+Parameter+Documentation
export function getPylonLink(obj: {
  logName: string;
  timestamp: string;
}): string {
  return `https://drivelogs.data.motional.com/pylon/index.html?logslice=${
    obj.logName
  }&timestamp=${Number(obj.timestamp) / 1e6}`;
}

export function getSceneHuntingLink(obj: {
  logName: string;
  timestamp: string;
  cameraChannel: string | undefined;
}): string {
  return `/scene-hunting?log=${obj.logName}&curTimeAbs=${obj.timestamp}&channel=${obj.cameraChannel}`;
}

const MetaData = styled.div`
  .label {
    margin-right: 1rem;
    margin-bottom: 0.5rem;
    display: inline-block;
  }

  div:not(:root):fullscreen & {
    display: block;
  }
`;

interface OTMetadataPanelProps {
  metadata: OmnitagResultItem;
  onClose: () => void;
}

export const OTMetadataPanel: FC<
  React.PropsWithChildren<OTMetadataPanelProps>
> = ({ metadata }) => {
  const timestampStr = metadata.timestamp.toString();

  const metadataTable1: KeyValueTableRow[] = useMemo(
    () => [
      {
        key: 'Log Name',
        value: metadata.log_name,
        maxStrLen: 19,
        allowCopy: true,
      },
      { key: 'Log Date', value: metadata.log_date },
      { key: 'Camera Channel', value: metadata.channel },
      { key: 'Timestamp', value: timestampStr, type: 'formattedTimesstamp' },
    ],
    [metadata, timestampStr]
  );

  const metadataTable2: KeyValueTableRow[] = useMemo(() => {
    const data = [
      { key: 'Distance to Ego', value: metadata.distance_to_ego },
      { key: 'Track ID', value: metadata.track_id },
      { key: 'Track Type', value: metadata.track_type },
      { key: 'IOU', value: metadata.iou },
      { key: 'Tag Probability', value: metadata.tag_probability },
      { key: 'Route Name', value: metadata.route_name },
      { key: 'City', value: metadata.city },
      { key: 'Test Type', value: metadata.test_type },
      { key: 'Test Sub Type', value: metadata.test_sub_type },
      { key: 'Software Version', value: metadata.software_version },
      { key: 'Sun Lighting Condition', value: metadata.sun_lighting_condition },
      { key: 'Weather Condition', value: metadata.weather_condition },
      { key: 'Surface Type', value: metadata.surface_type },
    ];
    if (metadata.ego_location) {
      data.push({ key: 'Ego Location', value: metadata.ego_location });
    }
    return data;
  }, [metadata]);

  return (
    <>
      <MetaData>
        <KeyValueTable
          tableNameKey="metadata-table-1"
          data={metadataTable1}
          keyColWidth={160}
          maxValueWidth={80}
          rowHeight={5}
        />
        <Typography.Title level={5} style={{ marginTop: '2em' }}>
          Metadata
        </Typography.Title>
        <KeyValueTable
          tableNameKey="metadata-table-2"
          data={metadataTable2}
          keyColWidth={160}
          maxValueWidth={80}
          rowHeight={5}
        />
      </MetaData>
    </>
  );
};
